import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '../contexts/AuthContext'
import {
  markAllSeenInList,
  markOneSeenInList,
  notificationsApi,
} from '../services/notificationsApi'
import type { AppNotification } from '../types/notifications'

const POLL_INTERVAL_MS = 60_000
const IMMEDIACY_DEBOUNCE_MS = 800

// Multiple consumers (e.g. the bell in two layouts) own independent state;
// broadcast mark-seen so they stay in sync without a reload. Mirrors
// RELEASE_SEEN_EVENT in useLastSeenRelease.
export const NOTIFICATIONS_SEEN_EVENT = 'evop:notifications-seen'

// Background events the badge should react to within ~1s rather than
// waiting up to a full poll interval.
const IMMEDIACY_EVENTS = ['evop:sync-completed', 'evop:sync-triggered']

export interface UseNotificationsResult {
  notifications: AppNotification[]
  unread: number
  loading: boolean
  error: boolean
  loadList: () => Promise<void>
  markSeen: (id: number) => Promise<void>
  markAllSeen: () => Promise<void>
  refresh: () => void
}

/**
 * Drives the header notifications bell: polls the unread count (only while
 * authenticated and the tab is visible), loads the list lazily on dropdown
 * open, and optimistically marks items seen.
 */
export function useNotifications(): UseNotificationsResult {
  const { isAuthenticated } = useAuth()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const cancelledRef = useRef(false)

  const refreshCount = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const count = await notificationsApi.unreadCount()
      if (!cancelledRef.current) setUnread(count)
    } catch {
      // Silent - the badge keeps its last good value on a poll failure.
    }
  }, [isAuthenticated])

  const loadList = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    setError(false)
    try {
      const rows = await notificationsApi.list({ limit: 20 })
      if (!cancelledRef.current) setNotifications(rows)
    } catch {
      // Surface an error so the dropdown can offer a Retry; keep the
      // last-known list so a transient blip doesn't blank the panel.
      if (!cancelledRef.current) setError(true)
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [isAuthenticated])

  // Poll the unread count, but only while authenticated and the tab is
  // visible. A hidden tab makes no requests; on becoming visible again it
  // refetches once to catch up.
  useEffect(() => {
    cancelledRef.current = false
    if (!isAuthenticated) {
      setUnread(0)
      setNotifications([])
      setError(false)
      return
    }

    let handle: number | undefined

    const isVisible = () => document.visibilityState === 'visible'

    const tick = () => {
      if (isVisible()) refreshCount()
    }

    const start = () => {
      if (handle === undefined) {
        handle = window.setInterval(tick, POLL_INTERVAL_MS)
      }
    }
    const stop = () => {
      if (handle !== undefined) {
        window.clearInterval(handle)
        handle = undefined
      }
    }

    const onVisibility = () => {
      if (isVisible()) {
        refreshCount()
        start()
      } else {
        stop()
      }
    }

    // Refetch when the window regains focus (tab/app switch) so the badge
    // is fresh the moment the user returns, ahead of the next poll tick.
    const onFocus = () => {
      if (isVisible()) refreshCount()
    }

    // Prime immediately if visible, then run the interval.
    if (isVisible()) {
      refreshCount()
      start()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)

    return () => {
      cancelledRef.current = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [isAuthenticated, refreshCount])

  // Immediacy: refetch the count shortly after a sync is triggered/completed
  // so the badge updates within ~1s instead of up to 45s.
  useEffect(() => {
    if (!isAuthenticated) return
    let debounce: number | undefined
    const handler = () => {
      if (debounce !== undefined) window.clearTimeout(debounce)
      debounce = window.setTimeout(() => refreshCount(), IMMEDIACY_DEBOUNCE_MS)
    }
    IMMEDIACY_EVENTS.forEach((name) => window.addEventListener(name, handler))
    return () => {
      if (debounce !== undefined) window.clearTimeout(debounce)
      IMMEDIACY_EVENTS.forEach((name) => window.removeEventListener(name, handler))
    }
  }, [isAuthenticated, refreshCount])

  // Cross-instance sync: when one bell marks things seen, others follow.
  useEffect(() => {
    const handler = () => refreshCount()
    window.addEventListener(NOTIFICATIONS_SEEN_EVENT, handler)
    return () => window.removeEventListener(NOTIFICATIONS_SEEN_EVENT, handler)
  }, [refreshCount])

  const markSeen = useCallback(async (id: number) => {
    // Optimistic: flip the row + drop the badge before the round-trip.
    setNotifications((prev) => markOneSeenInList(prev, id, new Date().toISOString()))
    setUnread((u) => Math.max(0, u - 1))
    try {
      await notificationsApi.markSeen(id)
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SEEN_EVENT))
    } catch {
      // Reconcile both the badge and the optimistically-stamped rows
      // against the server on failure.
      refreshCount()
      loadList()
    }
  }, [refreshCount, loadList])

  const markAllSeen = useCallback(async () => {
    const now = new Date().toISOString()
    setNotifications((prev) => markAllSeenInList(prev, now))
    setUnread(0)
    try {
      await notificationsApi.markAllSeen()
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SEEN_EVENT))
    } catch {
      refreshCount()
      loadList()
    }
  }, [refreshCount, loadList])

  return {
    notifications,
    unread,
    loading,
    error,
    loadList,
    markSeen,
    markAllSeen,
    refresh: refreshCount,
  }
}
